import React, { useEffect, useState } from 'react';
import { ICatalogPlus, CatalogViewMode } from 'Interfaces';
import { Helpers } from 'Helpers';
import { ThreeDots } from 'react-loader-spinner';
import { CatalogCard } from '../catalogCard/CatalogCard';
import { CatalogListRow } from '../catalogListRow/CatalogListRow';
import { useInView } from 'react-intersection-observer';
import { strings } from 'strings';
import './catalogGallery.scss';
import config from 'config';

type CatalogGalleryProps = {
    visibleCatalogs: ICatalogPlus[],
    hiddenCatalogCodes: Array<string>,
    isLoading: boolean,
    isOnline: boolean,
    theme: string,
    startFresh: number,
    baseUrl: string,
    tabIndex: number,
    viewMode: CatalogViewMode,
    gridSize: number,
    onCatalogAction?(action: string): void,
    onCatalogVisibilityChanged(catalog: ICatalogPlus): void,
    onRequestCatalogAccess(catalog: ICatalogPlus): void
}

export function CatalogGallery(props: CatalogGalleryProps) {
    const catalogPerPage: number = 20,
        loaderColor: string = config.loaderColor,
        isListView: boolean = props.viewMode === 'list',
        cardSize: number = props.gridSize,
        [pageOffset, setPageOffset] = useState<number>(0),
        [loadedCatalogs, setLoadedCatalogs] = useState<number>(catalogPerPage),
        [activeCardOffset, setActiveCardOffset] = useState<number>(0),
        [displayCatalogs, setDisplayCatalogs] = useState<Array<ICatalogPlus>>(props.visibleCatalogs.slice(0, loadedCatalogs)),
        { ref, inView, entry } = useInView({
            root: document.querySelector('#catalogListEntries'),
            rootMargin: '0px 0px 150px 0px',
            threshold: 0
        }),

        area: HTMLElement | null = document.getElementById('catalogListEntries'),
        areaRect: DOMRect | null = area ? area.getBoundingClientRect() : null,
        containerWidth: number = areaRect ? areaRect.width - 20 : cardSize,
        cardsPerRow: number = isListView ? 1 : Math.max(1, Math.floor(containerWidth / (cardSize + 16))),

        addCatalogToDisplay = () => {
            //load next page in 
            if (props.visibleCatalogs.length > displayCatalogs.length) {
                const nextPage: number = pageOffset + 1,
                    nextCatalogs: Array<ICatalogPlus> = props.visibleCatalogs.slice(nextPage * catalogPerPage, catalogPerPage + (nextPage * catalogPerPage)),
                    updaytedDisplayCatalogs: Array<ICatalogPlus> = displayCatalogs.concat(nextCatalogs);

                setDisplayCatalogs(updaytedDisplayCatalogs);
                setPageOffset(nextPage);
            }
        },
        handleCatalogVisibilityChanged = (catalog: ICatalogPlus) => {
            const cardElement: HTMLElement | null = document.getElementById(catalog.code);

            if (cardElement) {
                const cardRect: DOMRect = cardElement.getBoundingClientRect(),
                    offset: number = cardElement.offsetTop - cardRect.y + 113;

                setActiveCardOffset(offset);
                setLoadedCatalogs(displayCatalogs.length);                
            }

            props.onCatalogVisibilityChanged(catalog);
        };

    useEffect(() => {
        if (props.visibleCatalogs.length) {
            const updatedCatalog: Array<ICatalogPlus> = props.visibleCatalogs.slice(0, loadedCatalogs);
            
            setPageOffset(loadedCatalogs / catalogPerPage - 1);
            setDisplayCatalogs([...updatedCatalog]);           
        } else {
            setPageOffset(0);
            setDisplayCatalogs([]);
        }

    }, [props.visibleCatalogs]);

    useEffect(() => {
        if (inView) {
            addCatalogToDisplay();
        }
    }, [inView]);

    useEffect(() => {
        const parentDiv: HTMLElement | null = document.getElementById('catalogListEntries');

        if (activeCardOffset && parentDiv) {
            parentDiv.scrollTop = activeCardOffset;            
            setActiveCardOffset(0);
        }
    }, [displayCatalogs]);

    useEffect(() => {
        setLoadedCatalogs(catalogPerPage);
        const parentDiv: HTMLElement | null = document.getElementById('catalogListEntries');

        if (parentDiv) {
            parentDiv.scrollTop = 0;            
            setActiveCardOffset(0);
        }
    }, [props.startFresh]);

    return (
        <div className={`catalog-gallery__container nice-scrolling ${isListView ? 'catalog-gallery__container--list' : ''}`} id={`catalogListEntries`}
            style={!isListView ? { gridTemplateColumns: `repeat(auto-fill, ${cardSize + 16}px)`, gridTemplateRows: `repeat(auto-fill, ${Math.round(cardSize * (244 / 340)) + Math.round(170 * Math.max(0.6, Math.min(1, cardSize / 300))) + 16}px)` } : undefined}
        >
            {!props.isLoading && props.visibleCatalogs.length === 0 &&
                <div className="catalog-gallery__no-items">
                    {strings['catalog.entry.noResult']}
                </div>
            }
            {props.isLoading && !props.visibleCatalogs.length &&
                <div className="catalog-gallery__loader">
                    <ThreeDots color={loaderColor} height={80} width={80} />
                </div>
            }
            {isListView && displayCatalogs.length > 0 &&
                <div className='catalog-list-row__header'>
                    <div className='catalog-list-row__thumbnail' />
                    <div className='catalog-list-row__cell catalog-list-row__manufacturer'>{strings['catalog.column.manufacturer']}</div>
                    <div className='catalog-list-row__cell catalog-list-row__category'>{strings['catalog.column.category']}</div>
                    <div className='catalog-list-row__cell catalog-list-row__description'>{strings['catalog.column.description']}</div>
                    <div className='catalog-list-row__cell catalog-list-row__version'>{strings['catalog.column.version']}</div>
                    <div className='catalog-list-row__cell catalog-list-row__date'>{strings['catalog.column.lastActiveVersionDate']}</div>
                    <div className='catalog-list-row__cell catalog-list-row__actions'></div>
                </div>
            }
            {displayCatalogs.length > 0 && displayCatalogs.map((catalog: ICatalogPlus, index: number) => {

                const borderTop: boolean = index + 1 > cardsPerRow;
                const borderRight: boolean = (index % cardsPerRow !== 0) && (index < displayCatalogs.length ) ;
                const availabilityFlag = Helpers.getCatalogAvailabilityFlag(catalog);

                return isListView ? (
                    <CatalogListRow
                        key={catalog.id}
                        catalog={catalog}
                        isHidden={catalog.isHidden}
                        baseUrl={props.baseUrl}
                        tabIndex={props.tabIndex + index + 1}
                        availabilityFlag={availabilityFlag}
                        onCatalogVisibilityChanged={handleCatalogVisibilityChanged}
                        onRequestCatalogAccess={props.onRequestCatalogAccess}
                    />
                ) : (
                    <CatalogCard
                        key={catalog.id}
                        catalog={catalog}
                        hiddenCatalogCodes={props.hiddenCatalogCodes}
                        isHidden={catalog.isHidden}
                        isOnline={props.isOnline}
                        borderRight={borderRight}
                        borderTop={borderTop}
                        baseUrl={props.baseUrl}
                        cardSize={cardSize}
                        availabilityFlag={availabilityFlag}
                        tabIndex={props.tabIndex + index + 1}
                        onCatalogAction={props.onCatalogAction}
                        onCatalogVisibilityChanged={handleCatalogVisibilityChanged}
                        onRequestCatalogAccess={props.onRequestCatalogAccess}
                    />
                );
            })}
            {displayCatalogs.length > 0 &&
                <div ref={ref} className='item-gallery__loadMore'>{inView}</div>
            }
        </div>
    )
}